import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Pen } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { SortIcon } from '@/components/common/SortIcon';
import { DeleteDialog } from '@/components/common/Dialogs';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { queryKeys } from '@/lib/constants/queryKeys';
import type { Client, ClientFilters } from '../types/client.types';

interface GetColumnsParams {
  orderBy: string;
  order: 'asc' | 'desc' | '';
  onSort: (field: string) => void;
  filters: ClientFilters;
}

export function useClientColumns({
  orderBy,
  order,
  onSort,
  filters,
}: GetColumnsParams): ColumnDef<Client>[] {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasAccess = useAuthStore((state) => state.hasAccess);

  const columns: ColumnDef<Client>[] = [
    {
      accessorKey: 'name',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('name')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('name')}
        >
          <p>{t('name')}</p>
          <SortIcon active={orderBy === 'name'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <Link
          to={`/app/clients/${row.original.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.lastname} {row.original.name} {row.original.middlename}
        </Link>
      ),
    },
    {
      accessorKey: 'type',
      header: () => (
        <div
          className="flex items-center cursor-pointer select-none"
          onClick={() => onSort('type')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('type')}
        >
          <p>{t('type')}</p>
          <SortIcon active={orderBy === 'type'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.type ? t(`clientTypes.${row.original.type}`) : '-'}</div>,
    },
    {
      accessorKey: 'email',
      header: t('email'),
      cell: ({ row }) => <div>{row.original.email}</div>,
    },
    {
      accessorKey: 'phone',
      header: t('phone'),
      cell: ({ row }) => <div>{row.original.phone}</div>,
    },
    {
      accessorKey: 'address',
      header: t('address'),
      cell: ({ row }) => (
        <div>
          {row.original.country} {row.original.city} {row.original.region} {row.original.street}{' '}
          {row.original.zip}
        </div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={orderBy === 'createdAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const dateStr = row.original.createdAt;
        return <div>{format(new Date(dateStr), 'yyyy-MM-dd HH:mm')}</div>;
      },
    },
  ];

  // Add document column based on filter type
  const documentColumn = getDocumentColumn(filters, t);
  columns.splice(5, 0, documentColumn);

  // Add actions column if user has permissions
  if (hasAccess('client', 'update') || hasAccess('client', 'delete')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: t('actionsLabel'),
      cell: ({ row }) => {
        const data = row.original;
        return (
          <div className="flex">
            {hasAccess('client', 'update') && (
              <Button
                size="sm"
                onClick={() => navigate(`action${row.original.type}/${row.original.id}`)}
                variant="outline"
                className="rounded-r-none"
              >
                <Pen />
              </Button>
            )}
            {hasAccess('client', 'delete') && (
              <DeleteDialog
                id={data.id}
                title={data.name}
                url="clients"
                queryKey={queryKeys.clients.all}
              />
            )}
          </div>
        );
      },
    });
  }

  return columns;
}

function getDocumentColumn(filters: ClientFilters, t: (key: string) => string): ColumnDef<Client> {
  if (filters.type === 'PERSON') {
    return {
      accessorKey: 'document',
      header: t('pinfl'),
      cell: ({ row }) => (
        <div className="flex flex-col">
          <div className="font-medium">{row.original.pinfl}</div>
        </div>
      ),
    };
  }

  if (filters.type === 'COMPANY') {
    return {
      accessorKey: 'document',
      header: t('inn'),
      cell: ({ row }) => (
        <div className="flex flex-col">
          <div className="font-medium">{row.original.inn}</div>
        </div>
      ),
    };
  }

  // Default: show both
  return {
    accessorKey: 'document',
    header: `${t('inn')}/${t('pinfl')}`,
    cell: ({ row }) => {
      const { type, inn, pinfl } = row.original;
      return (
        <div className="flex flex-col">
          <div className="font-medium">
            {type === 'COMPANY' ? `${t('inn')}: ${inn || '-'}` : `${t('pinfl')}: ${pinfl || '-'}`}
          </div>
        </div>
      );
    },
  };
}
